import { IonButton, IonSpinner } from '@ionic/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { requestService, whatsappMetaCreateTemplate, whatsappMetaTemplateExample, whatsappMetaTemplates } from '../../../../../Utils/Services';
import './WhatsAppMetaCard.css';
import './WhatsAppTemplates.css';

type Suggestion = {
  key: string; name: string; title: string; description: string; group: string;
  category: string; language: string; body: string; headerFormat?: 'IMAGE';
  automaticTriggerActive?: boolean;
  variables: Array<{ position: number; field: string; label: string; example: string }>;
};
type Template = {
  id?: string; name: string; language: string; status: string; category: string;
  rejectedReason?: string; matchesCatalog?: boolean; components?: Array<{ type: string; text?: string; format?: string }>
};
type Assets = { wabaId: string; canEdit: boolean; catalog: Suggestion[]; templates: Template[]; nextCursor?: string | null; checkedAt: string };
type BatchResult = { key: string; title: string; message: string };
const statusLabels: Record<string, string> = { APPROVED: 'Aprovado', PENDING: 'Em análise', REJECTED: 'Rejeitado', PAUSED: 'Pausado', DISABLED: 'Desativado', IN_APPEAL: 'Em recurso', UNKNOWN: 'Status não informado' };
const statusLabel = (value: string) => statusLabels[value] || value;
function preview(suggestion: Suggestion) {
  return suggestion.body.replace(/\{\{(\d+)\}\}/g, (match, position) => suggestion.variables.find(item => item.position === Number(position))?.example || match);
}

const WhatsAppTemplates: React.FC<{ empresaId: string }> = ({ empresaId }) => {
  const history = useHistory();
  const [assets, setAssets] = useState<Assets | null>(null);
  const [selectedKey, setSelectedKey] = useState('');
  const [group, setGroup] = useState('Todos');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [uploadRef, setUploadRef] = useState('');
  const [imageName, setImageName] = useState('');
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchResults, setBatchResults] = useState<BatchResult[]>([]);
  const [batchProgress, setBatchProgress] = useState('');
  const stopBatch = useRef(false);
  const generation = useRef(0);
  const operation = useRef(false);
  const api = useCallback(<T,>(endpoint: { url: string; method: string }, body?: unknown, after?: string) => new Promise<T>((resolve, reject) => {
    const url = endpoint.url.replace('{empresaId}', encodeURIComponent(empresaId)) + (after ? `?after=${encodeURIComponent(after)}` : '');
    requestService(url, { method: endpoint.method, credentials: 'include', ...(body ? { body: JSON.stringify(body) } : {}) }, history,
      (value: any) => resolve(value as T), reject, true);
  }), [empresaId, history]);

  const refresh = useCallback(async (after?: string) => {
    if (operation.current) return;
    const current = generation.current;
    operation.current = true;
    setBusy(true); setError('');
    try {
      const result = await api<Assets>(whatsappMetaTemplates, undefined, after);
      if (generation.current !== current) return;
      if (!Array.isArray(result.catalog) || !Array.isArray(result.templates) || !result.wabaId) throw new Error('Resposta inválida ao consultar templates.');
      setAssets(previous => ({
        ...result, templates: after && previous?.wabaId === result.wabaId
          ? Array.from(new Map([...previous.templates, ...result.templates].map(item => [`${item.name}:${item.language}`, item])).values()) : result.templates
      }));
      setSelectedKey(previous => result.catalog.some(item => item.key === previous) ? previous : result.catalog[0]?.key || '');
    } catch (failure: any) {
      if (generation.current === current) setError(failure?.message || 'Não foi possível consultar os templates. Tente novamente.');
    } finally {
      if (generation.current === current) { operation.current = false; setBusy(false); }
    }
  }, [api]);

  useEffect(() => {
    generation.current += 1; operation.current = false;
    setAssets(null); setSelectedKey(''); setGroup('Todos'); setUploadRef(''); setImageName(''); setNotice('');
    setBatchRunning(false); setBatchResults([]); setBatchProgress(''); stopBatch.current = false;
    void refresh();
    return () => { generation.current += 1; };
  }, [empresaId, refresh]);

  const selected = assets?.catalog.find(item => item.key === selectedKey);
  const existing = assets?.templates.find(item => item.name === selected?.name && item.language === selected?.language);
  const canCreate = !!assets?.canEdit && !!selected && !existing && !busy && !error
    && (selected.headerFormat !== 'IMAGE' || !!uploadRef);
  const needsBatchImage = assets?.catalog.some(item => item.headerFormat === 'IMAGE'
    && !assets.templates.some(template => template.name === item.name && template.language === item.language));

  const upload = async (file?: File) => {
    if (!file || operation.current || !assets?.canEdit) return;
    setUploadRef(''); setImageName(''); setError(''); setNotice('');
    if (!['image/png', 'image/jpeg'].includes(file.type) || file.size > 1024 * 1024) { setError('Escolha uma imagem JPG ou PNG de até 1 MB.'); return; }
    const current = generation.current;
    operation.current = true; setBusy(true);
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader(); reader.onload = () => resolve(String(reader.result).split(',')[1]);
        reader.onerror = () => reject(new Error('Não foi possível ler a imagem.')); reader.readAsDataURL(file);
      });
      if (generation.current !== current) return;
      const result = await api<{ uploadRef: string }>(whatsappMetaTemplateExample, { mime: file.type, base64 });
      if (generation.current === current) { setUploadRef(result.uploadRef); setImageName(file.name); setNotice('Imagem enviada para análise do template. Crie o modelo em até 20 minutos.'); }
    } catch (failure: any) {
      if (generation.current === current) setError(failure?.message || 'Não foi possível enviar a imagem de exemplo.');
    } finally { if (generation.current === current) { operation.current = false; setBusy(false); } }
  };

  const create = async () => {
    if (!canCreate || !selected || operation.current) return;
    if (!window.confirm(`Enviar o template “${selected.title}” para análise da Meta na WABA ${assets?.wabaId}? Isso não envia mensagens aos clientes.`)) return;
    const current = generation.current;
    operation.current = true; setBusy(true); setError(''); setNotice('');
    try {
      const result = await api<{ reused: boolean; template: Template }>(whatsappMetaCreateTemplate, { templateKey: selected.key, ...(uploadRef ? { uploadRef } : {}) });
      if (generation.current !== current) return;
      if (!result.template?.name || !result.template?.status) throw new Error('A criação não foi confirmada. Atualize o status antes de tentar novamente.');
      setAssets(previous => previous ? { ...previous, templates: [...previous.templates.filter(item => item.name !== result.template.name || item.language !== result.template.language), { ...result.template, matchesCatalog: true }] } : previous);
      setNotice(`${result.reused ? 'Template existente localizado' : 'Template enviado à Meta'}. Status: ${statusLabel(result.template.status)}. Nenhum disparo foi ativado.`);
    } catch (failure: any) {
      if (generation.current === current) setError(failure?.message || 'A criação não foi confirmada. Atualize o status antes de tentar novamente.');
    } finally { if (generation.current === current) { operation.current = false; setBusy(false); } }
  };

  const createAll = async () => {
    if (!assets?.canEdit || operation.current || error || (needsBatchImage && !uploadRef)) return;
    if (!window.confirm(`Criar todos os templates ausentes do catálogo na WABA ${assets.wabaId}? Os existentes serão mantidos. A imagem de exemplo será usada nos modelos com imagem. Isso não envia mensagens aos clientes. Mantenha esta tela aberta até concluir.`)) return;
    const current = generation.current;
    operation.current = true; stopBatch.current = false;
    setBusy(true); setBatchRunning(true); setError(''); setNotice(''); setBatchResults([]);
    setBatchProgress('Conferindo todos os templates da conta…');
    try {
      // Read all pages before deciding what is missing. Never follow Meta paging URLs.
      let after: string | undefined;
      const cursors = new Set<string>();
      const known = new Map<string, Template>();
      do {
        const page = await api<Assets>(whatsappMetaTemplates, undefined, after);
        if (generation.current !== current) return;
        if (!page.canEdit || page.wabaId !== assets.wabaId || !Array.isArray(page.templates)) throw new Error('A conexão ou a permissão mudou. Atualize o status antes de continuar.');
        page.templates.forEach(item => known.set(`${item.name}:${item.language}`, item));
        after = page.nextCursor || undefined;
        if (after && (cursors.has(after) || cursors.size >= 100)) throw new Error('Não foi possível concluir a consulta dos templates.');
        if (after) cursors.add(after);
      } while (after && !stopBatch.current);
      if (stopBatch.current) { setNotice('Criação interrompida. Nenhum novo template será enviado.'); return; }
      setAssets(previous => previous ? { ...previous, templates: Array.from(known.values()), nextCursor: null } : previous);
      for (let index = 0; index < assets.catalog.length; index += 1) {
        const item = assets.catalog[index];
        if (generation.current !== current) return;
        if (stopBatch.current) break;
        setBatchProgress(`${index + 1} de ${assets.catalog.length}: ${item.title}`);
        const found = known.get(`${item.name}:${item.language}`);
        let message: string;
        if (found) {
          message = found.matchesCatalog === false ? 'Mantido sem alterações: conteúdo diferente, revise na Meta.' : `Já cadastrado — ${statusLabel(found.status)}`;
        } else {
          try {
            if (item.headerFormat === 'IMAGE' && !uploadRef) throw new Error('Envie a imagem de exemplo antes de continuar.');
            const result = await api<{ reused: boolean; template: Template }>(whatsappMetaCreateTemplate,
              { templateKey: item.key, ...(item.headerFormat === 'IMAGE' ? { uploadRef } : {}) });
            if (generation.current !== current) return;
            if (result.template?.name !== item.name || result.template?.language !== item.language || !result.template?.status) throw new Error('A criação não foi confirmada. Atualize o status antes de tentar novamente.');
            known.set(`${item.name}:${item.language}`, { ...result.template, matchesCatalog: true });
            setAssets(previous => previous ? { ...previous, templates: Array.from(known.values()) } : previous);
            message = `${result.reused ? 'Já cadastrado' : 'Enviado'} — ${statusLabel(result.template.status)}`;
          } catch (failure: any) {
            if (generation.current !== current) return;
            setBatchResults(previous => [...previous, { key: item.key, title: item.title, message: 'Não confirmado — atualize o status antes de retomar.' }]);
            throw failure;
          }
        }
        setBatchResults(previous => [...previous, { key: item.key, title: item.title, message }]);
      }
      setNotice(stopBatch.current ? 'Criação interrompida. Os templates já enviados foram mantidos.' : 'Catálogo processado. Confira o resultado de cada template abaixo. Nenhum disparo foi ativado.');
    } catch (failure: any) {
      if (generation.current === current) setError(failure?.message || 'Lote interrompido. Atualize o status antes de retomar.');
    } finally {
      if (generation.current === current) { operation.current = false; setBusy(false); setBatchRunning(false); setBatchProgress(''); }
    }
  };

  return <div className="wa-templates">
    <header>
      {/* <h1>Gerenciar Templates</h1> */}
      <p>Cadastre os modelos padrão do Xeguei na conta WhatsApp Business conectada e acompanhe a análise da Meta.</p>
      {assets && <p>WABA da empresa: <strong>{assets.wabaId}</strong></p>}
      <div className="wa-template-actions">
        <IonButton fill="outline" disabled={busy} onClick={() => void refresh()}>Atualizar status</IonButton>
        {assets?.canEdit && <IonButton disabled={busy || !!error || (!!needsBatchImage && !uploadRef)} onClick={() => void createAll()}>Criar todos os templates na Meta</IonButton>}
      </div>
    </header>
    <div className="wa-notice">Fase 1: cadastro e consulta. Criar ou aprovar templates não altera os envios atuais nem ativa campanhas. A aprovação e a categoria final são definidas pela Meta.</div>
    {busy && <div role="status"><IonSpinner /> Aguarde a operação com a Meta…</div>}
    {error && <p role="alert" className="wa-template-error">{error} Use “Atualizar status” para conferir o resultado antes de reenviar.</p>}
    {notice && <p role="status" className="wa-connection-status">{notice}</p>}
    {assets && <>
      {!assets.canEdit && <p className="wa-notice">Você tem acesso de leitura. A criação de templates exige permissão de edição das configurações.</p>}
      {assets.canEdit && needsBatchImage && <label className="wa-template-image wa-template-batch-image">Imagem de exemplo para o lote (JPG ou PNG, até 1 MB)
        <input type="file" accept="image/jpeg,image/png" disabled={busy} onChange={event => void upload(event.target.files?.[0])} />
        <small>Selecione uma imagem para habilitar “Criar todos”. Ela será usada nos modelos com imagem, independentemente do filtro. Válida por 20 minutos após o envio. Não inclua dados pessoais.</small>
        {imageName && <span>Imagem selecionada: {imageName}</span>}
      </label>}
      {batchRunning && <div className="wa-template-batch-progress"><p role="status">{batchProgress}</p><p>Mantenha esta tela aberta. Ao sair, o lote para após a solicitação em andamento.</p>
        <IonButton fill="outline" onClick={() => { stopBatch.current = true; setBatchProgress('Interrompendo após a solicitação em andamento…'); }}>Interromper criação</IonButton>
      </div>}
      {batchResults.length > 0 && <section className="wa-template-batch-results" aria-label="Resultado da criação em lote"><h2>Resultado do lote ({batchResults.length}/{assets.catalog.length})</h2>
        <ul>{batchResults.map(item => <li key={item.key}><strong>{item.title}</strong> — {item.message}</li>)}</ul>
      </section>}
      <label className="wa-template-filter">Filtrar por módulo <select value={group} disabled={busy} onChange={event => setGroup(event.target.value)}>
        {['Todos', ...Array.from(new Set(assets.catalog.map(item => item.group)))].map(item => <option key={item}>{item}</option>)}
      </select></label>
      <div className="xeguei-review-template-layout">
        <div className="wa-template-catalog" role="group" aria-label="Catálogo Xeguei">
          {assets.catalog.filter(item => group === 'Todos' || item.group === group).map(item => {
            const found = assets.templates.find(template => template.name === item.name && template.language === item.language);
            return <button type="button" key={item.key} disabled={busy} aria-pressed={item.key === selectedKey} onClick={() => { setSelectedKey(item.key); setNotice(''); }}>
              <strong>{item.title}</strong><span>{item.group} · {item.category === 'UTILITY' ? 'Utilidade' : 'Marketing'} · {item.headerFormat === 'IMAGE' ? 'Com imagem' : 'Texto'}</span>
              <small>{found ? statusLabel(found.status) : assets.nextCursor ? 'Ainda não localizado nas páginas carregadas' : 'Não cadastrado'}</small>
            </button>;
          })}
        </div>
        {selected && <section className="xeguei-review-template-preview" aria-label="Prévia do template">
          <h2>{selected.title}</h2><code className="xeguei-review-template-name">{selected.name}</code>
          <p>{selected.description}</p><p>Categoria sugerida: {selected.category} · Idioma: {selected.language}</p>
          <div className="xeguei-review-chat-bubble template">{preview(selected)}</div>
          <h3>Variáveis no envio</h3><ul>{selected.variables.map(variable => <li key={variable.position}>{`{{${variable.position}}}`} — {variable.label} (exemplo: {variable.example})</li>)}</ul>
          <p>Os valores acima são exemplos para aprovação, não dados dos seus clientes.</p>
          {existing ? <div className="wa-notice"><strong>Status na Meta: {statusLabel(existing.status)}</strong><p>Categoria atual: {existing.category}</p>
            {existing.rejectedReason && <p>Motivo informado pela Meta: {existing.rejectedReason}</p>}
            {existing.matchesCatalog === false && <p>Este nome já está em uso com conteúdo diferente do catálogo. Revise o modelo na Meta; o Xeguei não o sobrescreverá.</p>}
            {['REJECTED', 'PAUSED', 'DISABLED'].includes(existing.status) && <p>Revise este template no Gerenciador do WhatsApp da Meta antes de utilizá-lo.</p>}
          </div> : <>
            {selected.headerFormat === 'IMAGE' && <label className="wa-template-image">Imagem de exemplo (JPG ou PNG, até 1 MB)
              <input key={selected.key} type="file" accept="image/jpeg,image/png" disabled={busy || !assets.canEdit} onChange={event => void upload(event.target.files?.[0])} />
              {imageName && <span>{imageName}</span>}
              <small>A imagem será enviada à Meta para avaliar o modelo. Não envie dados pessoais de clientes.</small>
            </label>}
            <IonButton expand="block" disabled={!canCreate} onClick={() => void create()}>Criar template na Meta</IonButton>
          </>}
        </section>}
      </div>
      <section className="wa-template-existing"><h2>Templates da conta conectada ({assets.templates.length})</h2>
        {assets.templates.length === 0 && <p>Nenhum template encontrado nesta página.</p>}
        {assets.templates.map(item => <div key={`${item.name}:${item.language}`}><code>{item.name}</code><span>{statusLabel(item.status)} · {item.category} · {item.language}</span>
          {item.rejectedReason && <small>{item.rejectedReason}</small>}</div>)}
        {assets.nextCursor && <IonButton disabled={busy} onClick={() => void refresh(assets.nextCursor || undefined)}>Carregar mais templates</IonButton>}
      </section>
    </>}
  </div>;
};
export default WhatsAppTemplates;
